import { useCallback, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';

import type { LocationAnswer } from '@anyroad/google-maps';
import Input from 'views/ui/form_elements/input';
import DateInput from 'views/ui/form_elements/date_input';
import Dropdown from 'views/ui/form_elements/dropdown';
import Radios from 'views/ui/form_elements/radios';
import MultiSelect from 'views/ui/form_elements/multi_select_boxes';
import NPS from 'views/ui/nps_score';
import { Location } from 'views/ui/form_elements/location';

import { Waiver } from '../waivers';
import {
  QuestionsContainer,
  QuestionItem,
  QuestionLabel,
  QuestionSubtitle,
  QuestionSubtext,
  InputWrapper,
} from './styles';

import {
  getAnswerForQuestion,
  getAnswersForQuestion,
  getErrorsForQuestion,
  processInputValue,
  extractPositionFromName,
  createOptionMap,
  processMultiSelectValues,
  getDateMask,
} from './Helpers';

// Type definitions
interface QuestionOption {
  text: string;
  value?: string | number;
}

interface Question {
  id: string | number;
  position: number;
  text: string;
  subtitle?: string;
  subtext?: string;
  input_type:
    | 'text'
    | 'select'
    | 'date'
    | 'radio'
    | 'multi'
    | 'nps'
    | 'waiver'
    | 'location';
  answer?: string | number | boolean | string[] | number[];
  options?: QuestionOption[];
  date_format?: string;
  location_answer?: LocationAnswer | null;
}

interface ErrorsMap {
  get: (key: string) => string[] | undefined;
}

interface QuestionsProps {
  questions: Question[];
  onChange: (
    position: number,
    value: string | number | boolean | (string | number)[] | LocationAnswer
  ) => void;
  googleTagManagerCheckoutProgress?: (step: number) => void;
  errors: ErrorsMap;
  renderBottomBorder?: boolean;
}

export const Questions = ({
  questions,
  onChange,
  googleTagManagerCheckoutProgress,
  errors,
  renderBottomBorder = false,
}: QuestionsProps) => {
  const { formatMessage } = useIntl();
  const questionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (googleTagManagerCheckoutProgress && questionsRef.current) {
      const events = ['focus', 'click'];
      const multiEventListener = () => {
        googleTagManagerCheckoutProgress(4);
        events.forEach((event) => {
          questionsRef.current?.removeEventListener(event, multiEventListener, {
            capture: true,
          });
        });
      };
      events.forEach((event) => {
        questionsRef.current?.addEventListener(event, multiEventListener, {
          capture: true,
        });
      });
    }
  }, [googleTagManagerCheckoutProgress]);

  const answerForQuestion = useCallback((question: Question) => {
    return getAnswerForQuestion(question);
  }, []);

  const answersForQuestion = useCallback((question: Question) => {
    return getAnswersForQuestion(question);
  }, []);

  const inputChanged = useCallback(
    (position: number, _name: string, value: string | number | boolean) => {
      const processedValue = processInputValue(value);
      onChange(position, processedValue);
    },
    [onChange]
  );

  const optionSelected = useCallback(
    (name: string, value: string | number) => {
      const position = extractPositionFromName(name);
      if (position) {
        onChange(position, value);
      }
    },
    [onChange]
  );

  const errorsForQuestion = useCallback(
    (question: Question) => {
      return getErrorsForQuestion(question, errors);
    },
    [errors]
  );

  const radioClicked = useCallback(
    (position: number, itemValue: string | number | (string | number)[]) => {
      onChange(position, itemValue);
    },
    [onChange]
  );

  const multiClicked = useCallback(
    (
      values: (string | number | null)[],
      question: Question,
      itemValue: string | number,
      itemPosition: number
    ) => {
      const newValues = processMultiSelectValues(
        values,
        itemValue,
        itemPosition
      );
      radioClicked(
        question.position,
        newValues.filter((v): v is string | number => v !== null) as (
          | string
          | number
        )[]
      );
    },
    [radioClicked]
  );

  const renderLabels = useCallback((question: Question) => {
    return (
      <>
        <QuestionLabel>{question.text}</QuestionLabel>
        {Boolean(question?.subtitle) && (
          <QuestionSubtitle>
            <span>{question?.subtitle}</span>
          </QuestionSubtitle>
        )}
      </>
    );
  }, []);

  const renderAsTextInput = useCallback(
    (question: Question) => {
      const handleInputChange = (_name: string, value: string) => {
        inputChanged(question.position, _name, value);
      };

      return (
        <InputWrapper>
          {renderLabels(question)}
          <Input
            data-testid="text-question-input"
            value={answerForQuestion(question)}
            name={`answer-${question.position}`}
            errors={errorsForQuestion(question)}
            onChange={handleInputChange}
            maxLength="5000"
          />
          {question.subtext && (
            <QuestionSubtext
              dangerouslySetInnerHTML={{ __html: question.subtext }}
            />
          )}
        </InputWrapper>
      );
    },
    [renderLabels, answerForQuestion, errorsForQuestion, inputChanged]
  );

  const renderAsDateInput = useCallback(
    (question: Question) => {
      const handleDateChange = (_name: string, value: string) => {
        inputChanged(question.position, _name, value);
      };

      return (
        <InputWrapper>
          {renderLabels(question)}
          <DateInput
            mask={getDateMask(question.date_format || '')}
            data-testid="date-question-input"
            placeholder={question.date_format || ''}
            style={{ letterSpacing: '2px' }}
            value={answerForQuestion(question)}
            name={`answer-${question.position}`}
            errors={errorsForQuestion(question)}
            onChange={handleDateChange}
            maxLength="150"
          />
          {question.subtext && (
            <QuestionSubtext
              dangerouslySetInnerHTML={{ __html: question.subtext }}
            />
          )}
        </InputWrapper>
      );
    },
    [renderLabels, answerForQuestion, errorsForQuestion, inputChanged]
  );

  const renderAsSelectInput = useCallback(
    (question: Question) => {
      const optionMap = createOptionMap(question.options || []);
      const dropdownProps = {
        'data-testid': 'select-question-input',
        name: `answer-${question.position}`,
        errors: errorsForQuestion(question),
        options: optionMap,
        value: answerForQuestion(question),
        icon: <i className="af-triangle-down-2 icon icon-anyfont" />,
        placeholder: formatMessage({
          defaultMessage: 'Select one',
          description: 'Placeholder text for dropdown selection field',
        }),
        onChange: optionSelected,
        theme: 'checkout',
        subtext: question.subtext,
      };

      return (
        <InputWrapper>
          {renderLabels(question)}
          <Dropdown {...dropdownProps} />
          {question.subtext && (
            <QuestionSubtext
              dangerouslySetInnerHTML={{ __html: question.subtext }}
            />
          )}
        </InputWrapper>
      );
    },
    [renderLabels, answerForQuestion, errorsForQuestion, optionSelected]
  );

  const renderAsRadioInput = useCallback(
    (question: Question) => {
      const handleRadioClick = (itemValue: string | number) => {
        radioClicked(question.position, itemValue);
      };

      return (
        <InputWrapper>
          {renderLabels(question)}
          <Radios
            data-testid="radio-question-input"
            items={question.options || []}
            errors={errorsForQuestion(question)}
            selectedValue={answerForQuestion(question)}
            onClick={handleRadioClick}
            subtext={question.subtext}
          />
        </InputWrapper>
      );
    },
    [renderLabels, answerForQuestion, errorsForQuestion, radioClicked]
  );

  const renderAsMultiInput = useCallback(
    (question: Question) => {
      const answers = answersForQuestion(question);
      const handleMultiClick = (
        itemValue: string | number,
        itemPosition: number
      ) => {
        multiClicked(answers, question, itemValue, itemPosition);
      };

      return (
        <InputWrapper>
          {renderLabels(question)}
          <MultiSelect
            data-testid="radio-question-input"
            items={question.options || []}
            errors={errorsForQuestion(question)}
            selectedAnswers={answers}
            onClick={handleMultiClick}
            subtext={question.subtext}
          />
        </InputWrapper>
      );
    },
    [renderLabels, answersForQuestion, errorsForQuestion, multiClicked]
  );

  const renderAsWaiver = useCallback(
    (question: Question) => {
      return (
        <InputWrapper>
          <Waiver
            data-testid="waiver-question-input"
            question={question}
            value={answerForQuestion(question)}
            onChange={(value: string | number | boolean) =>
              inputChanged(question.position, '', value)
            }
            errors={errorsForQuestion(question)}
          />
        </InputWrapper>
      );
    },
    [answerForQuestion, errorsForQuestion, inputChanged]
  );

  const renderAsNpsScale = useCallback(
    (question: Question) => {
      const handleNpsClick = (itemValue: string | number) => {
        radioClicked(question.position, itemValue);
      };

      return (
        <InputWrapper>
          {renderLabels(question)}
          <NPS
            data-testid="nps-question-input"
            items={question.options || []}
            errors={errorsForQuestion(question)}
            selectedValue={answerForQuestion(question)}
            onClick={handleNpsClick}
          />
          {question.subtext && (
            <QuestionSubtext
              dangerouslySetInnerHTML={{ __html: question.subtext }}
            />
          )}
        </InputWrapper>
      );
    },
    [renderLabels, answerForQuestion, errorsForQuestion, radioClicked]
  );

  const renderAsLocation = useCallback(
    (question: Question) => (
      <Location
        error={errorsForQuestion(question)?.[0] ?? undefined}
        label={question.text || ''}
        subtext={question.subtext || ''}
        position={question.position}
        value={question.location_answer ?? undefined}
        onChange={onChange}
      />
    ),
    [errorsForQuestion, onChange]
  );

  const renderQuestion = useCallback(
    (question: Question, index: number) => {
      const { id, input_type: inputType } = question;
      const isFirst = index === 0;
      const isLast = index === questions.length - 1;

      return (
        <QuestionItem
          key={id}
          isFirst={isFirst}
          isLast={isLast}
          hasBottomBorder={renderBottomBorder}
          data-testid={`checkout-question-${index}`}
        >
          <div className={`row ${inputType}`}>
            {inputType === 'text' && renderAsTextInput(question)}
            {inputType === 'select' && renderAsSelectInput(question)}
            {inputType === 'date' && renderAsDateInput(question)}
            {inputType === 'radio' && renderAsRadioInput(question)}
            {inputType === 'multi' && renderAsMultiInput(question)}
            {inputType === 'nps' && renderAsNpsScale(question)}
            {inputType === 'waiver' && renderAsWaiver(question)}
            {inputType === 'location' && renderAsLocation(question)}
          </div>
        </QuestionItem>
      );
    },
    [
      questions.length,
      renderBottomBorder,
      renderAsTextInput,
      renderAsSelectInput,
      renderAsDateInput,
      renderAsRadioInput,
      renderAsMultiInput,
      renderAsNpsScale,
      renderAsWaiver,
      renderAsLocation,
    ]
  );

  return (
    <QuestionsContainer ref={questionsRef} data-testid="checkout-questions">
      {questions.map(renderQuestion)}
    </QuestionsContainer>
  );
};
